import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Loader2, User } from "lucide-react";
import { updateCustomers } from "@/services/auth";
import { fetchCustomerData } from "@/redux/slices/customerSlice";
import toast from "react-hot-toast";
import { getT } from "@/i18n/translations";


export default function ProfileSetup() {
  const t = getT();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [villages, setVillages] = useState([]);

  const mobile =
    localStorage.getItem("mobile") ||
    localStorage.getItem("phone") ||
    localStorage.getItem("phoneNumber") || "";

  const [form, setForm] = useState({
    name: "", pin: "", state: "", district: "", village: "", landowned: "",
  });

  const fetchPincodeData = async (pin) => {
    if (pin.length !== 6) return;
    setPincodeLoading(true);
    setVillages([]);
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const data = await res.json();
      if (data[0]?.Status === "Success" && data[0]?.PostOffice?.length > 0) {
        const postOffices = data[0].PostOffice;
        const po = postOffices[0];
        const villageNames = [...new Set(postOffices.map((p) => p.Name))];
        setVillages(villageNames);
        setForm((prev) => ({
          ...prev,
          state: po.State || "",
          district: po.District || "",
          village: villageNames[0] || "",
        }));
      } else {
        toast.error(t.profile_invalidPincode);
      }
    } catch {
      toast.error(t.profile_pincodeError);
    }
    setPincodeLoading(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    const val = name === "pin" ? value.replace(/\D/g, "").slice(0, 6) : value;
    setForm((prev) => ({ ...prev, [name]: val }));
    if (name === "pin" && val.length === 6) fetchPincodeData(val);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error(t.profile_errName);
      return;
    }

    if (!form.pin || form.pin.length !== 6) {
      toast.error(t.profile_errPincode);
      return;
    }

    if (!form.state || !form.district) {
      toast.error(t.profile_errLocation);
      return;
    }

    if (!form.landowned) {
      toast.error(t.profile_errAge);
      return;
    }

    setLoading(true);

    try {
      const token = JSON.parse(localStorage.getItem("token_gen"));
      const customerCode = localStorage.getItem("customercode");

      const response = await updateCustomers(
        token?.access_token,
        customerCode,
        {
          name: form.name.trim(),
          state: form.state,
          district: form.district,
          village: form.village,
          pin: form.pin,
          landowned: Number(form.landowned),
        }
      );

      if (response?.message === "Request processed successfully.") {
        toast.success(t.profile_saved);

        await dispatch(fetchCustomerData(customerCode));

        localStorage.setItem("isNewUser", "false");

        navigate("/home");
      } else {
        toast.error(t.profile_saveError);
      }
    } catch (error) {
      toast.error(t.profile_saveError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: "#fff5f5",
        backgroundImage:
          "radial-gradient(circle, #ffd6d6 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    >
      {/* LOADER */}
      {loading && (
        <div className="fixed inset-0 bg-black/30 flex justify-center items-center z-50 backdrop-blur-sm">
          <Loader2 className="animate-spin h-10 w-10 text-[#e60012]" />
        </div>
      )}

      <div className="w-full max-w-md min-h-screen flex flex-col bg-[#ffe5e5]">

        {/* HEADER */}
        <div className="relative flex items-center justify-center px-4 py-4 mt-2">
          <p className="text-[15px] font-bold text-[#e60012] tracking-wide">
            {t.profile_heading}
          </p>
        </div>

        {/* CONTENT */}
        <div className="px-4 pb-8 space-y-4 flex-1">

          {/* PROFILE CARD */}
          <div className="bg-white rounded-3xl px-5 py-5 shadow-md mx-1 flex items-center gap-4 border border-red-100">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center shrink-0 shadow-sm"
              style={{
                background:
                  "linear-gradient(135deg, #e60012 0%, #b3000f 100%)",
              }}
            >
              <User size={24} className="text-white" />
            </div>

            <div>
              <p className="text-[14px] font-bold text-[#b3000f]">
                {mobile}
              </p>

              <p className="text-[11px] text-gray-400">
                {t.profile_subheading}
              </p>
            </div>
          </div>

          {/* FORM CARD */}
          <div className="bg-white rounded-3xl px-5 py-5 shadow-md mx-1 space-y-5 border border-red-100">

            {/* NAME */}
            <div>
              <p className="text-[13px] font-semibold text-[#b3000f] mb-2">
                {t.profile_nameLabel}
              </p>

              <input
                name="name"
                value={form.name}
                onChange={handleFormChange}
                placeholder={t.profile_namePlaceholder}
                className="w-full border border-red-100 rounded-2xl px-4 py-3 text-[14px] text-[#111] outline-none focus:border-[#e60012] transition-colors placeholder:text-red-200 bg-red-50/30"
              />
            </div>

            {/* PINCODE */}
            <div>
              <p className="text-[13px] font-semibold text-[#b3000f] mb-2">
                {t.profile_pincodeLabel}
              </p>

              <div className="relative">
                <input
                  name="pin"
                  value={form.pin}
                  onChange={handleFormChange}
                  placeholder={t.profile_pincodePlaceholder}
                  inputMode="numeric"
                  maxLength={6}
                  className="w-full border border-red-100 rounded-2xl px-4 py-3 text-[14px] text-[#111] outline-none focus:border-[#e60012] transition-colors placeholder:text-red-200 bg-red-50/30 pr-10"
                />

                {pincodeLoading && (
                  <Loader2 className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#e60012]" />
                )}
              </div>
            </div>

            {/* STATE */}
            <div>
              <p className="text-[13px] font-semibold text-[#b3000f] mb-2">
                {t.profile_stateLabel}
              </p>

              <input
                name="state"
                value={form.state}
                onChange={handleFormChange}
                placeholder={t.profile_autoFill}
                className="w-full border border-red-100 rounded-2xl px-4 py-3 text-[14px] text-[#111] outline-none focus:border-[#e60012] transition-colors placeholder:text-red-200 bg-red-50/30"
              />
            </div>

            {/* DISTRICT */}
            <div>
              <p className="text-[13px] font-semibold text-[#b3000f] mb-2">
                {t.profile_districtLabel}
              </p>

              <input
                name="district"
                value={form.district}
                onChange={handleFormChange}
                placeholder={t.profile_autoFill}
                className="w-full border border-red-100 rounded-2xl px-4 py-3 text-[14px] text-[#111] outline-none focus:border-[#e60012] transition-colors placeholder:text-red-200 bg-red-50/30"
              />
            </div>

            {/* VILLAGE */}
            <div>
              <p className="text-[13px] font-semibold text-[#b3000f] mb-2">
                {t.profile_villageLabel}
              </p>

              {villages.length > 0 ? (
                <select
                  name="village"
                  value={form.village}
                  onChange={handleFormChange}
                  className="w-full border border-red-100 rounded-2xl px-4 py-3 text-[14px] text-[#111] outline-none focus:border-[#e60012] transition-colors bg-red-50/30 appearance-none"
                >
                  {villages.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  name="village"
                  value={form.village}
                  onChange={handleFormChange}
                  placeholder={t.profile_autoFill}
                  className="w-full border border-red-100 rounded-2xl px-4 py-3 text-[14px] text-[#111] outline-none focus:border-[#e60012] transition-colors placeholder:text-red-200 bg-red-50/30"
                />
              )}
            </div>

            {/* LAND */}
            <div>
              <p className="text-[13px] font-semibold text-[#b3000f] mb-2">
                {t.profile_ageLabel}
              </p>

              <input
                name="landowned"
                value={form.landowned}
                onChange={handleFormChange}
                placeholder={t.profile_acreagePlaceholder}
                inputMode="numeric"
                className="w-full border border-red-100 rounded-2xl px-4 py-3 text-[14px] text-[#111] outline-none focus:border-[#e60012] transition-colors placeholder:text-red-200 bg-red-50/30"
              />
            </div>
          </div>

          {/* INFO NOTE */}
          <div className="rounded-2xl px-4 py-3 mx-1 flex items-start gap-2 bg-red-50 border border-red-100">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#e60012"
              strokeWidth="2"
              className="mt-0.5 shrink-0"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>

            <p className="text-[11px] text-[#b3000f] leading-relaxed">
              {t.profile_infoNote}
            </p>
          </div>

          {/* SAVE BUTTON */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-4 rounded-2xl text-[15px] font-bold text-white active:scale-[0.98] transition-transform disabled:opacity-50 shadow-md"
            style={{
              width: "calc(100% - 8px)",
              marginLeft: "4px",
              background:
                "linear-gradient(135deg, #e60012 0%, #b3000f 100%)",
            }}
          >
            {t.profile_saveBtn}
          </button>
        </div>

        {/* FOOTER */}
        {/* <div className="w-full py-3 border-t">
          <p className="text-[13px] text-red-600 uppercase text-center tracking-wide font-semibold">
            © {new Date().getFullYear()} Consumer Journeys. All Rights Reserved.
          </p>
        </div> */}
      </div>
    </div>
  );
}